import React, { useContext, useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { add, filter } from 'lodash';
import { sentenceCase } from 'change-case';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// icons
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';

// components
import ZakazkyForm from '../../sections/@dashboard/zakazky/ZakazkyForm';

import { Modal, ModalBody } from '../../components/modal/Modal';
import Label from '../../components/label';
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';

import { defaultDate } from '../../utils/formatTime';

// sections
import { ZakazkyListHead, ZakazkyListToolbar } from '../../sections/@dashboard/zakazky';
// mock
import ZAKAZKYCONTEXT from '../../_mock/zakazky';

import Map from '../../components/maps/Map';

import { app } from '../../utils/app.config';

import { ZakazkyContext } from '../../services/zakazkyContext';
import PositionPicker from '../../components/maps/PositionPicker';
import { useAlert } from '../../hooks/useAlert';
import { useFazy } from '../../hooks/useFazy';
import CopyToClipboard from '../../components/clipboard/CopyToClipboard';
import { useMemo } from 'react';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'number', label: 'Čislo stavby', alignRight: false },
  { id: 'clientName', label: 'Zákazník', alignRight: false },
  { id: 'place', label: 'Miesto výstavby', alignRight: false },
  { id: 'district', label: 'Okres', alignRight: false },
  { id: 'gps', label: 'GPS', alignRight: false },
  { id: 'map', label: 'Mapa', alignRight: false },
  { id: 'faza', label: 'Fáza výstavby', alignRight: false },
  { id: 'stav', label: 'Stav stavby', alignRight: false },
  { id: 'dateEnd', label: 'Dátum ukončenia', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  if (orderBy === 'number') {
    return order === 'desc' ? (a, b) => numberDescendingComparator(a, b) : (a, b) => -numberDescendingComparator(a, b);
  } else {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }
}

export function numberDescendingComparator(a, b) {
  const regex = /^(\d+)\/(\d{4})$/; // corrected regex for '/'
  const aMatch = a.number && a.number.match(regex);
  const bMatch = b.number && b.number.match(regex);

  if (!aMatch || !bMatch) {
    return 0;
  }

  const aYear = parseInt(aMatch[2], 10);
  const bYear = parseInt(bMatch[2], 10);

  const aNumber = parseInt(aMatch[1], 10);
  const bNumber = parseInt(bMatch[1], 10);

  if (aYear !== bYear) {
    return bYear - aYear;
  }

  return bNumber - aNumber;
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return b[1] - a[1];
  });
  if (query) {
    return filter(array, (_user) => {
      return (
        (_user?.clientName && _user?.clientName?.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_user?.number && _user?.number?.toString().toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_user?.place && _user?.place?.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_user?.district && _user?.district?.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_user?.stav && _user?.stav?.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_user?.faza && _user?.faza?.toLowerCase().indexOf(query.toLowerCase()) !== -1)
      );
    });
  }
  return stabilizedThis.map((el) => el[0]);
}

const extractCityFromGeocodeResults = (result) => {
  for (let i = 0; i < result.address_components.length; i++) {
    const addressComponent = result.address_components[i];
    const types = addressComponent.types;

    if (types.includes('locality') || types.includes('administrative_area_level_1')) {
      return addressComponent.long_name;
    }
  }

  return null;
};

export default function ZakazkyListPage() {
  const [mapPosition, setMapPosition] = useState(null);
  const [openPicker, setOpenPicker] = useState(false);
  const { iconSet, statusSet, getEndDate, getStartingYear, getStartingMonth } = useFazy();
  const [filterYear, setFilterYear] = useState(null);
  const [filterFaza, setFilterFaza] = useState(null);
  const navigate = useNavigate();

  const [currentZakazka, setCurrentZakazka] = useState(null);

  const mapRef = useRef(null);

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('number');

  const [filterName, setFilterName] = useState('');

  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);

  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [modal, setModal] = useState(false);

  const { zakazky, loading, error, addZakazka, removeZakazka } = useContext(ZakazkyContext);

  const { success, error: errorAlert, confirm } = useAlert();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const { hash } = window.location;
    if (hash === '#mapa') {
      mapRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  useEffect(() => {
    if (searchParams.get('show')) {
      setPage(0);
    }
    if (searchParams.get('page')) {
      setPage(Number(searchParams.get('page')));
    }
    if (searchParams.get('rows')) {
      setRowsPerPage(Number(searchParams.get('rows')));
    }
    if (searchParams.get('faza')) {
      setFilterFaza(searchParams.get('faza'));
    } else {
      setFilterFaza(null);
    }
    if (searchParams.get('year')) {
      setFilterYear(searchParams.get('year'));
    } else {
      setFilterYear(null);
    }
  }, [searchParams]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = ZAKAZKYCONTEXT.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const removeZakazkaHandler = async (id) => {
    removeZakazka(id);
  };

  const handleChangePage = (event, newPage) => {
    localStorage.setItem('zakazkyPage', newPage);
    setPage(newPage);
    setSearchParams((params) => {
      params.set('page', newPage);
      return params;
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    localStorage.setItem('zakazkyPage', 0);
    localStorage.setItem('zakazkyRows', parseInt(event.target.value, 10));
    setRowsPerPage(parseInt(event.target.value, 10));
    setSearchParams((params) => {
      params.set('page', 0);
      params.set('rows', parseInt(event.target.value, 10));
      return params;
    });
  };

  const handlerYearChange = (e) => {
    setPage(0);
    setSearchParams((params) => {
      params.set('page', 0);
      return params;
    });
    setFilterYear(e);
    setSearchParams((params) => {
      if (e === null) {
        params.delete('year');
        return params;
      }
      params.set('year', e);
      return params;
    });
  };

  const handleFazaChange = (e) => {
    setPage(0);
    setSearchParams((params) => {
      params.set('page', 0);
      return params;
    });
    setFilterFaza(e);
    setSearchParams((params) => {
      if (e === null) {
        params.delete('faza');
        return params;
      }
      params.set('faza', e);
      return params;
    });
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const getQueriedZakazky = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    switch (searchParams.get('show')) {
      case 'year':
        return zakazky?.filter((item) => {
          return item?.year == currentYear;
        });
      case 'month':
        return zakazky?.filter((item) => {
          const startingMonth = getStartingMonth(item);
          const startingYear = getStartingYear(item);

          return startingMonth === currentMonth && startingYear === currentYear;
        });
      case 'progress':
        return zakazky?.filter((item) => {
          return statusSet(item) === 'V priebehu';
        });
      case 'hruba':
        return zakazky?.filter((item) => item.faza === 'hrubá stavba');
      case 'holodom':
        return zakazky?.filter((item) => item.faza === 'holodom');
      case 'kluc':
        return zakazky?.filter((item) => item.faza === 'na kľúč');
      default:
        return zakazky;
    }
  };

  const getFilteredZakazkyByYear = () => {
    const filteredZakazky = getQueriedZakazky();
    if (filterYear) {
      return filteredZakazky?.filter((zakazka) => {
        return zakazka?.year == filterYear;
      });
    }
    return filteredZakazky;
  };

  const getFilteredZakazkyByFaza = () => {
    const filteredZakazkyByYear = getFilteredZakazkyByYear();
    if (filterFaza) {
      return filteredZakazkyByYear?.filter((zakazka) => {
        return filterFaza === zakazka.faza;
      });
    }
    return filteredZakazkyByYear;
  };

  const filteredAllZakazky = applySortFilter(getFilteredZakazkyByFaza(), getComparator(order, orderBy), filterName);

  return (
    <>
      <Helmet>
        <title> Zoznam zakázok | {app.title} </title>
      </Helmet>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Zoznam zakázok/stavieb
          </Typography>
          <Button variant="contained" onClick={() => setModal(true)} startIcon={<Iconify icon="eva:plus-fill" />}>
            Pridať zakázku
          </Button>
        </Stack>
        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
          open={modal}
          onClose={() => setModal(false)}
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <>
            <ZakazkyForm
              setOpenPicker={setOpenPicker}
              mapPosition={mapPosition}
              setMapPosition={setMapPosition}
              currentZakazka={currentZakazka}
              setLat={setLat}
              setLng={setLng}
              lat={lat}
              lng={lng}
            />
          </>
        </Modal>

        <Card>
          <ZakazkyListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            year={filterYear}
            onYearChange={handlerYearChange}
            faza={filterFaza}
            onFazaChange={handleFazaChange}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ZakazkyListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={zakazky.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredAllZakazky?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { id, number, clientName, place, district, faza, dateEnd, lat, lng, stav } = row;
                    const selectedUser = selected.indexOf(clientName) !== -1;

                    return (
                      <TableRow hover key={id} tabIndex={-1} role="checkbox" selected={selectedUser}>
                        <TableCell component="th" scope="row" padding="none">
                          <Typography variant="subtitle2" noWrap sx={{ pl: 2 }}>
                            {number ? <Link to={`detail/${id}`}>{number}</Link> : '-'}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">{clientName}</TableCell>

                        <TableCell align="left">{!place ? '-' : place}</TableCell>

                        <TableCell align="left">{!district ? '-' : district}</TableCell>

                        <TableCell align="left">{lat && lng ? <CopyToClipboard lat={lat} lng={lng} /> : '-'}</TableCell>

                        <TableCell align="left">
                          {lat && lng ? (
                            <a href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`} target="_blank">
                              <OpenInNewRoundedIcon />
                            </a>
                          ) : (
                            '-'
                          )}
                        </TableCell>

                        <TableCell align="left">{faza ? iconSet(faza, 12) : '-'}</TableCell>
                        <TableCell align="center">{row && statusSet(row)}</TableCell>
                        <TableCell align="left">
                          {row && defaultDate(getEndDate(row)) !== 'Invalid Date' ? defaultDate(getEndDate(row)) : '-'}
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            aria-label="delete"
                            color="primary"
                            onClick={() => navigate(`/sprava/zakazky/detail/${id}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton aria-label="delete" color="error" onClick={() => removeZakazkaHandler(id)}>
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/*emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )*/}
                </TableBody>

                {loading && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <CircularProgress />
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={filteredAllZakazky.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={'Počet položiek na jednej strane'}
            labelDisplayedRows={({ from, to, count }) => {
              return '' + from + '-' + to + ' z ' + count;
            }}
          />
        </Card>
      </Container>

      <Container ref={mapRef}>
        <Typography variant="h4" gutterBottom mt={5}>
          Mapa všetkých stavieb
        </Typography>
        <Card sx={{ p: 1, mt: 2, mb: 2, minHeight: 600 }}>
          {' '}
          <Map
            zoom={8}
            position={{ lat: 48.669, lng: 19.699 }}
            height="600px"
            zakazky={filteredAllZakazky}
            providedYear={filterYear}
          />
        </Card>
      </Container>

      <Modal
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
        open={openPicker}
        onClose={() => setOpenPicker(false)}
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <ModalBody>
          <PositionPicker
            position={{ lat: 49.201614395271974, lng: 21.652639645246314 }}
            setMarkerPosition={setMapPosition}
            markerPosition={mapPosition}
            setLat={setLat}
            setLng={setLng}
          />

          <Button variant="outlined" onClick={() => setOpenPicker(false)}>
            Zvoliť polohu
          </Button>
        </ModalBody>
      </Modal>
    </>
  );
}
